// A signed-in account keeps its last System, Light or Dark (TOM-944), so a new
// browser or device opens in it. GET /v1/me reads it as `screenLook`; PATCH
// /v1/me saves it. Night is never saved: it stays on the device where it was
// picked, and a device showing Night keeps it when the account's look arrives.
import { useEffect, useRef, type MutableRefObject } from 'react';

import { api } from '../api';
import { onScreenLookChange, readStoredLook, setScreenLook, type ScreenLook } from './screenLook';

export type AccountLook = Exclude<ScreenLook, 'night'>;

/**
 * What to do when an account's look meets this browser's saved look at sign-in
 * or page load: show the account's look here, save this browser's choice to an
 * account that never chose, or nothing.
 */
export function settleAccountLook(
  account: AccountLook | null,
  stored: ScreenLook | null
): { apply?: AccountLook; save?: AccountLook } {
  if (stored === 'night') return {};
  if (account !== null) return account === stored ? {} : { apply: account };
  if (stored !== null) return { save: stored };
  return {};
}

/** Saves `look` to the account; `known` is what the account holds, as far as this tab knows. */
function saveAccountLook(look: AccountLook, known: MutableRefObject<AccountLook | null>): void {
  known.current = look;
  api('/v1/me', { method: 'PATCH', body: { screenLook: look } }).catch(() => {
    // The look still shows here; the next choice tries again.
    if (known.current === look) known.current = null;
  });
}

/**
 * Keeps the signed-in account's look and this browser's in step. Pass the
 * account's id and its `screenLook` from GET /v1/me; `undefined` (an older
 * server) or a null id does nothing.
 */
export function useAccountScreenLook(
  userId: string | null,
  accountLook: AccountLook | null | undefined
): void {
  // The look the account holds, as far as this tab knows, so a look this tab
  // applies from the account is not saved straight back.
  const known = useRef<AccountLook | null>(null);
  // Settled once per signed-in account: a later /v1/me read can land after a
  // choice made here and must not undo it.
  const settledFor = useRef<string | null>(null);

  useEffect(() => {
    if (userId === null || accountLook === undefined) return;
    if (settledFor.current === userId) return;
    settledFor.current = userId;
    known.current = accountLook;
    const action = settleAccountLook(accountLook, readStoredLook());
    if (action.apply !== undefined) setScreenLook(action.apply);
    if (action.save !== undefined) saveAccountLook(action.save, known);
  }, [userId, accountLook]);

  useEffect(() => {
    if (userId === null || accountLook === undefined) return;
    return onScreenLookChange((look) => {
      if (look === 'night' || look === known.current) return;
      saveAccountLook(look, known);
    });
  }, [userId, accountLook]);

  useEffect(() => {
    if (userId === null) settledFor.current = null;
  }, [userId]);
}
